<template>
  <VDialogV3
    ref="dialog"
    id="transport-order-card"
    show-edit-button
    height="1200px"
    width="1400px"
    :context-menu="TransportOrdersService.contextMenu"
    :on-context-menu-action="onContextMenuAction"
    :entity="localTransportOrder"
    :submit-method="submit"

    @edit-button:clicked="showTransportOrderRequisitesDialog"
    @close="onClose"
  >
    <template #badges>
      <VBadge v-if="localTransportOrder.isNew === true" class="primary">Новая</VBadge>
      <VBadge v-if="localTransportOrder.isOfferBased === false" class="primary">Прямой</VBadge>
      <VBadge v-if="localTransportOrder.hasDowntime" class="warning">Есть простои</VBadge>
      <VBadge v-if="localTransportOrder.isDeleted" class="warning">Удалён</VBadge>
      <VBadge>{{ localTransportOrder.typeString }}</VBadge>
    </template>

    <header class="document-header">

      <dl>
        <dt>Грузоотправитель</dt> <dd>{{ localTransportOrder?.senderString }}</dd>
        <dt>Пункт отправки</dt> <dd>{{ localTransportOrder?.senderStore?.address || '--' }}</dd>
      </dl>

      <dl>
        <dt>Грузополучатель</dt>
        <dd>{{ localTransportOrder?.receiverString }}</dd>
        <dt>Пункт доставки</dt>
        <dd>{{ localTransportOrder?.receiverStore?.address || '--' }}</dd>
      </dl>

    </header>
    <header class="document-header">
      <dl>
        <dt>Груз</dt>
        <dd :title="localTransportOrder.getCargoUnitsStringRepresentation()">
          {{ localTransportOrder.getCargoUnitsStringRepresentation() }}
        </dd>
        <dt>ТК</dt>
        <dd>{{ localTransportOrder.deliverer?.name || '--' }},
          {{ localTransportOrder.trackingNumber || '--' }}
        </dd>
        <dt>Доставка</dt>
        <dd>
          <template v-if="localTransportOrder?.storeTemperatureRange?.isEmpty() === false">t°
            {{ localTransportOrder.storeTemperatureRange }} °C
          </template>
          <template v-else>без температурных ограничений</template>
        </dd>

      </dl>

      <dl>
        <dt>Ответственный (получатель)</dt>
        <dd>{{ localTransportOrder.responsibleReceiver?.name || '--' }}</dd>
        <dt>Задача</dt>
        <dd>
          <a
            v-if="localTransportOrder.swPortalTask && localTransportOrder.isSingleSender"
            :href="'https://cp.siberianhealth.com/task3/view.php?id=' + localTransportOrder.swPortalTask.id"
            target="_blank"
          >
            № {{ localTransportOrder.swPortalTask.id }}
          </a>
          <template v-else>--</template>
        </dd>
        <dt>Дедлайн</dt>
        <dd>{{ $core.utils.date.format(localTransportOrder.deadline) || '--' }}</dd>
      </dl>

      <aside class="totals">
        <section v-if="!localTransportOrder.isSingleSender">
          <header>Стоимость перевозки</header>
          <dl>
            <dt class="total">План</dt>
            <dd>
              {{ localTransportOrder.transportCostPlanned?.format($core.currentPartner?.country.localCurrency.code) || '--' }}
            </dd>
            <dt class="total">Факт</dt>
            <dd>
              {{ localTransportOrder.transportCostActual?.format($core.currentPartner?.country.localCurrency.code) || '--' }}
            </dd>
          </dl>
        </section>

        <section>
          <header>Объявленная ценность</header>
          <dl>
            <dt class="total">Итого</dt>
            <dd class="total">
              {{ localTransportOrder.declaredCost?.format(localTransportOrder.declaredCostCurrencyCode) || '--' }}
            </dd>
          </dl>
        </section>
      </aside>
    </header>


    <header
      v-if="!localTransportOrder.isSingleSender"
      class="document-header"
    >
      <dl class="fill">
        <dt>Транспорт</dt>
        <dd>
          <CargoCapacity
            v-if="localTransportOrder.vehicle"
            :vehicle="localTransportOrder.vehicle"
            :cargo-weight-gross="localTransportOrder.cargoWeightGross"
            :cargo-units-count="localTransportOrder.cargoUnitsCount"
            :cargo-volume="localTransportOrder.cargoVolume"
          />
          <template v-else>--</template>
        </dd>
      </dl>
    </header>


    <!-- Assembly и Shipment — двухуровневый грид -->
    <template v-if="showShipmentDocumentsGrid">
      <h2>Отгрузочные документы</h2>
      <hr>
      <ShipmentDocumentsGrid
        class="fill"
        :transport-order="localTransportOrder"
        @document:updated="reloadTransportOrder"
      />
      <hr>
    </template>

    <!-- Вложенные ЗТК — двухуровневый грид -->
    <template v-if="showTransportOrdersGrid">
      <h2>Вложенные ЗТК</h2>
      <hr>
      <TransportOrdersGrid class="fill" :transport-order="localTransportOrder"/>
      <hr>
    </template>

    <!-- OrderPosition и Product — плоский грид -->
    <template v-if="showProductsGrid">
      <h2>{{ localTransportOrder?.isInternal ? 'Продукты' : 'Позиции заказа' }}</h2>
      <hr>
      <ProductsGrid class="fill" :transport-order="localTransportOrder"/>
      <hr>
    </template>

    <!-- Паллетоместа -->
    <CargoUnitsGrid
      v-if="showCargoUnitsGrid"
      :cargo-units-owner="localTransportOrder"
    />

    <template #footer>
      <VButton
        theme="primary"
        :disabled="submitErrorText !== null"
        @click="$refs.dialog.submit()"
      >
        {{ localTransportOrder.isExisting ? 'Сохранить' : 'Создать' }}
      </VButton>

      <VButton
        :disabled="localTransportOrder?.isExisting === false"
        @click="addDowntime"
      >
        Добавить простой +
      </VButton>

      <div v-if="submitErrorText" class="error">
        <VIcon material="priority_high"/>
        {{ submitErrorText }}
      </div>
    </template>
  </VDialogV3>
</template>

<style lang="scss" src="./TransportOrderCard.scss"/>

<script lang="ts">

import TransportOrder         from '@/domains/TransportOrders/TransportOrder';
import TransportOrdersService from '@/domains/TransportOrders/TransportOrdersService';
import TransportDowntime      from "@/domains/TransportDowntime/TransportDowntime";
import TransportDowntimesService from "@/domains/TransportDowntime/TransportDowntimesService";
import DriversService         from '@/domains/Drivers/DriversService';
import DriverFeedbacksService from '@/domains/Drivers/DriverFeedbacksService';
import type Driver            from '@/domains/Drivers/Driver';

import ShipmentDocumentsGrid  from "@/domains/TransportOrders/components/ShipmentDocumentsGrid.vue";
import TransportOrdersGrid    from "@/domains/TransportOrders/components/TransportOrdersGrid.vue";
import ProductsGrid           from "@/domains/TransportOrders/components/ProductsGrid.vue";
import CargoUnitsGrid         from "@/domains/CargoUnits/components/CargoUnitsGrid/CargoUnitsGrid.vue";
import CargoCapacity          from "@/domains/TransportOrders/components/CargoCapacity/CargoCapacity.vue";

export default defineComponent({
  name: 'TransportOrderCard',

  components: {
    CargoCapacity,
    CargoUnitsGrid,
    ShipmentDocumentsGrid,
    TransportOrdersGrid,
    ProductsGrid,
  },

  props: {
    id: {
      type: Number,
      default: null,
    },
    transportOrder: {
      type: TransportOrder,
    },
  },

  setup() {
    return {
      TransportOrdersService
    }
  },

  data() {
    return {
      localTransportOrder: undefined as unknown as TransportOrder,
      toolbarContextMenuVisible: false,
      localSWPortalTask: null as any,
      receivers: null as any[] | null,
      /** В карточке сохраняли вложенный документ или простой — ЗТК на бэке изменилась, хост должен обновиться */
      isDocumentUpdated: false,
    };
  },

  computed: {
    submitErrorText(): string | null {
      if (this.localTransportOrder?.isExisting === false) {
        if (!this.localTransportOrder?.pickupDatePlanned) return 'Не указана плановая дата забора груза';
        if (!this.localTransportOrder?.arrivalDatePlanned) return 'Не указана плановая дата прибытия груза';
        if (!this.localTransportOrder?.responsibleReceiver) return 'Не указан ответственный от получателя';
      }
      return null;
    },

    showShipmentDocumentsGrid(): boolean {
      return this.localTransportOrder.isSingleSender
        || this.localTransportOrder.isConsolidated;
    },

    showTransportOrdersGrid(): boolean {
      return this.localTransportOrder.isConsolidated;
    },

    showProductsGrid(): boolean {
      return this.localTransportOrder.isOrderPositionPickup
        || this.localTransportOrder.isReturn
        || this.localTransportOrder.isService
        || this.localTransportOrder.isInternal;
    },

    showCargoUnitsGrid(): boolean {
      return this.localTransportOrder.type !== 'service';
    }
  },

  async created() {
    await this.init();
  },

  methods: {
    async init() {
      if (this.id) {
        this.localTransportOrder = await TransportOrdersService.get(this.id)
      } else if (this.transportOrder) {
        this.localTransportOrder = this.$core.utils.cloneDeep(this.transportOrder);
      } else {
        throw new Error('Ошибка инициализации')
      }

      // Водитель. Данных о водителе ЗТК не отдаёт — у существующей перечитываем его по id.
      // Новой ЗТК сразу подставляем избранного водителя пользователя, у обычной поля водителя нет.
      // До открытия реквизитов: там водитель уже должен стоять
      if (this.localTransportOrder.isExisting === true) {
        if (this.$core.utils.isNullOrUndefined(this.localTransportOrder.driver?.id) === false) {
          this.localTransportOrder.driver = await DriversService.get(this.localTransportOrder.driver.id);
        }
        else {
          // водитель в ЗТК не указан
        }
      }
      else if (this.localTransportOrder.isSingleSender === false
        && this.$core.utils.isNullOrUndefined(this.localTransportOrder.driver) === true) {
        this.localTransportOrder.driver = await DriversService.getFavorite();
        this.localTransportOrder.fillVehicleFromDriver();
      }
      else {
        // обычная ЗТК либо водитель уже задан
      }

      if (this.localTransportOrder.isExisting === false) {
        this.showTransportOrderRequisitesDialog();
      }

      await this.localTransportOrder.calculateDeclaredCost();
    },

    /**
     * Сохранили вложенный отгрузочный документ — бэк пересчитал по нему ЗТК
     * (температурный режим и прочее), перечитываем её целиком.
     * Несохранённые правки в карточке при этом теряются.
     * Новую ЗТК перечитывать неоткуда — бэк посчитает её при создании.
     */
    async reloadTransportOrder(): Promise<void> {
      if (this.localTransportOrder.isExisting === true) {
        const transportOrder = await TransportOrdersService.get(this.localTransportOrder.id);

        // Данных о водителе ЗТК не отдаёт — перечитываем его по id, как в init
        if (this.$core.utils.isNullOrUndefined(transportOrder.driver?.id) === false) {
          transportOrder.driver = await DriversService.get(transportOrder.driver.id);
        }
        else {
          // водитель в ЗТК не указан
        }

        await transportOrder.calculateDeclaredCost();

        // Подменяем одним присваиванием, когда ЗТК уже собрана, — без промежуточного состояния в шаблоне
        this.localTransportOrder = transportOrder;

        this.isDocumentUpdated = true;
      }
      else {
        // новая ЗТК — на бэке её ещё нет
      }
    },

    /**
     * Закрытие пользователем (крестик, Escape). Если сохраняли вложенный документ —
     * ЗТК на бэке изменилась, закрываем через resolve, чтобы реестр обновился.
     * Иначе диалог закроет сам VDialogV3 (hideIfOpen).
     */
    onClose(): void {
      if (this.isDocumentUpdated === true) {
        this.$core.dialogs.TransportOrderCard.resolve();
      }
      else {
        // ничего не сохраняли — обычное закрытие
      }
    },

    /**
     * Пункт контекстного меню что-то изменил. Удалённую ЗТК сразу закрываем —
     * хост получит true и обновится, остальное перечитываем.
     */
    async onContextMenuAction(): Promise<void> {
      if (this.localTransportOrder.isDeleted === true) {
        this.$core.dialogs.TransportOrderCard.resolve();
      }
      else {
        await this.reloadTransportOrder();
      }
    },

    /**
     * Сохранение ЗТК. Вызывается диалогом через submitMethod: валидацию формы,
     * блокировку от повторного нажатия и resolve() при успехе делает VDialogV3.
     */
    async submit(): Promise<boolean> {
      let success = true;

      if (this.localTransportOrder.isExisting === true) {
        // update ЗТК сам сохраняет водителя и свою оценку — отдельных запросов не шлём
        success = await TransportOrdersService.update(this.localTransportOrder);
      }
      else {
        // Сначала водитель и своя оценка, потом ЗТК — разные запросы: ЗТК не сохранить, пока водитель не создан.
        // Нового создаём, у выбранного сохраняем правки полей водителя из формы. Транспорт и госномер — реквизиты ЗТК, водителя они не меняют.
        // После сохранения перечитываем водителя: ЗТК нужен его id, а при повторном сабмите — свежая версия.
        // ЗТК уходит с водителем без изменений — бэк его повторно не сохраняет
        const driver = this.localTransportOrder.driver;

        if (this.$core.utils.isNullOrUndefined(driver) === false) {
          // Своя оценка из реквизитов — запоминаем до перечитывания водителя, с бэка она придёт старой
          const feedback = driver.feedback;
          let savedDriver: Driver | null = null;
          let isDriverChanged: boolean;

          if (driver.isExisting === false) {
            isDriverChanged = true;

            // Водитель создаётся неявно из ЗТК — запоминаем за ним машину и госномер этой ЗТК
            driver.vehicle            = this.localTransportOrder.vehicle;
            driver.registrationNumber = this.localTransportOrder.registrationNumber;
          }
          else {
            savedDriver = await DriversService.get(driver.id);

            // Сравниваем всё, что правит форма водителя: иначе правки затрёт перечитывание после сохранения оценки
            isDriverChanged = driver.name !== savedDriver.name
              || driver.licenseNumber !== savedDriver.licenseNumber
              || driver.phone !== savedDriver.phone
              || driver.inn !== savedDriver.inn
              || driver.vehicleOwnershipType?.id !== savedDriver.vehicleOwnershipType?.id
              || driver.isFavorite !== savedDriver.isFavorite;
          }

          // Без выбранных звёзд сохранять нечего — бэк оценку обязательной требует.
          // Оценки ещё не было — любая выбранная звезда уже изменение
          let isFeedbackChanged: boolean;

          if (this.$core.utils.isNullOrUndefined(feedback?.rate) === true) {
            isFeedbackChanged = false;
          }
          else if (this.$core.utils.isNullOrUndefined(savedDriver?.feedback) === true) {
            isFeedbackChanged = true;
          }
          else {
            isFeedbackChanged = feedback.rate !== savedDriver.feedback.rate
              || feedback.comment !== savedDriver.feedback.comment;
          }

          if (isDriverChanged === true) {
            const driverId = await DriversService.upsert(driver);

            if (driverId === null) {
              success = false;
            }
            else {
              this.localTransportOrder.driver = await DriversService.get(driverId);
            }
          }
          else {
            // водитель не менялся — сохранять нечего
          }

          // Оценке нужен id водителя — сохраняем после водителя и перечитываем его, чтобы оценка пришла свежей
          if (success === true && isFeedbackChanged === true) {
            feedback.driver = this.localTransportOrder.driver;

            success = await DriverFeedbacksService.upsert(feedback);

            if (success === true) {
              this.localTransportOrder.driver = await DriversService.get(this.localTransportOrder.driver.id);
            }
            else {
              // оценку сохранить не удалось — ЗТК не сохраняем
            }
          }
          else {
            // водителя сохранить не удалось либо оценка не менялась
          }
        }
        else {
          // водитель в ЗТК не указан
        }

        if (success === true) {
          success = await TransportOrdersService.add(this.localTransportOrder, this.receivers ?? [], this.localSWPortalTask);
        }
        else {
          // водителя или оценку сохранить не удалось — ЗТК не сохраняем
        }
      }

      return success;
    },

    showTransportOrderRequisitesDialog(): void {
      this.$core.dialogs.TransportOrderRequisitesDialog.show({
        transportOrder: this.localTransportOrder,
        swPortalTask: this.localSWPortalTask,
        receivers: this.receivers,
      }, {
        save: ({transportOrder, SWPortalTask, receivers}: any) => {
          this.localTransportOrder = transportOrder;
          this.localSWPortalTask = SWPortalTask;
          this.receivers = receivers;
        },
        [TransportDowntime.Events.Updated]: (transportOrder) => {
          this.localTransportOrder = transportOrder;
          this.isDocumentUpdated   = true;
        }
      });
    },

    /** Простой сохраняется сам — по результату перечитываем ЗТК, чтобы он появился в ней */
    async addDowntime(): Promise<void> {
      const downtime = TransportDowntime.fromTransportOrder(this.localTransportOrder.id);

      if (await TransportDowntimesService.showTransportDowntimeCard(downtime) === true) {
        await this.reloadTransportOrder();
      }
    },


  },
});
</script>
